/**
 * useConstellationTaskSummary — Aggregate task status counts per workflow instance
 * for a constellation. Drives Ring 3 status overlays in constellation instance mode.
 *
 * DRR-126 Phase 2: Ring 3 deliverable nodes show PENDING/IN_PROGRESS/COMPLETED/OVERDUE
 * aggregate status derived from the actual Task primitives recorded against each
 * workflow instance. No new DynamoDB storage — derived query only.
 *
 * Data flow:
 *   Task.constellation_id + Task.instance_id
 *   → constellationTaskSummary(constellationId) resolver
 *   → GqlConstellationTaskSummary { byInstance: GqlInstanceTaskSummary[] }
 *   → keyed by instanceId for O(1) ring node lookup
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const CONSTELLATION_TASK_SUMMARY_QUERY = gql`
  query ConstellationTaskSummary($constellationId: String!) {
    constellationTaskSummary(constellationId: $constellationId) {
      constellationId
      total
      pending
      inProgress
      completed
      overdue
      byInstance {
        instanceId
        pending
        inProgress
        completed
        overdue
        failed
        aggregateStatus
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InstanceStatusSummary {
  instanceId: string;
  pending: number;
  inProgress: number;
  completed: number;
  overdue: number;
  failed: number;
  /** Derived aggregate: "overdue" | "in_progress" | "completed" | "pending" */
  aggregateStatus: string;
}

interface QueryData {
  constellationTaskSummary: {
    constellationId: string;
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    overdue: number;
    byInstance: InstanceStatusSummary[];
  };
}

export interface ConstellationTaskSummaryResult {
  /** Per-instance status map — key is workflow instance ID (ring_3 instanceId) */
  byInstance: Map<string, InstanceStatusSummary>;
  total: number;
  loading: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch task status summary for all workflow instances in a constellation.
 * Returns a Map keyed by `instanceId` for O(1) Ring 3 node lookup.
 *
 * @param constellationId - Constellation instance ID
 * @param skip - Set true to skip query (e.g. when not in instance mode)
 */
export function useConstellationTaskSummary(
  constellationId: string | undefined,
  skip = false,
): ConstellationTaskSummaryResult {
  const { data, loading, error } = useQuery<QueryData>(
    CONSTELLATION_TASK_SUMMARY_QUERY,
    {
      variables: { constellationId: constellationId ?? '' },
      skip: skip || !constellationId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const byInstance = useMemo<Map<string, InstanceStatusSummary>>(() => {
    const map = new Map<string, InstanceStatusSummary>();
    const instances = data?.constellationTaskSummary?.byInstance;
    if (!instances) return map;
    for (const inst of instances) {
      map.set(inst.instanceId, inst);
    }
    return map;
  }, [data]);

  return {
    byInstance,
    total: data?.constellationTaskSummary?.total ?? 0,
    loading,
    error: error as Error | undefined,
  };
}
